import {
    type ReactNode,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type AnimatedIconAnimation,
    type AnimatedIconTrigger,
    type NCoreUIKitIcon,
    NCoreUIKitTheme,
    AnimatedIcon,
    Button,
    Switch,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    CircleCheck as CircleCheckIcon,
    RefreshCw as RefreshCwIcon,
    EyeClosed as EyeClosedIcon,
    Sparkles as SparklesIcon,
    ThumbsUp as ThumbsUpIcon,
    Trash2 as Trash2Icon,
    Search as SearchIcon,
    Heart as HeartIcon,
    type LucideIcon,
    Menu as MenuIcon,
    Star as StarIcon,
    Bell as BellIcon,
    Zap as ZapIcon,
    Eye as EyeIcon,
    X as XIcon
} from "lucide-react-native";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "AnimatedIcon";

const USAGE_CODE = `import {
    Bell as BellIcon
} from "lucide-react-native";

<AnimatedIcon
    animation="wiggle"
    icon={BellIcon}
/>

<AnimatedIcon
    animationTrigger="hover"
    animation="signature"
    icon={BellIcon}
/>

<AnimatedIcon
    isTransitioned={isOpen}
    iconTransition="morph"
    fromIcon={MenuIcon}
    toIcon={XIcon}
/>`;

const TRIGGER_CODE = `const HoverIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <AnimatedIcon
    customColor={customColor}
    animationTrigger="hover"
    animation="signature"
    icon={Trash2Icon}
    color={color}
    size={size}
/>;

<Button
    onPress={handleDelete}
    icon={HoverIcon}
    title="Delete"
/>`;

const DELAY_CODE = `<AnimatedIcon
    animationDelay={index * durations.short / 2}
    onAnimationEnd={handleAnimationEnd}
    animation="appear"
    icon={icon}
/>`;

const ANIMATIONS: Array<{
    animation: AnimatedIconAnimation;
    icon: LucideIcon;
}> = [
    {
        animation: "appear",
        icon: SparklesIcon
    },
    {
        animation: "pop",
        icon: HeartIcon
    },
    {
        animation: "wiggle",
        icon: BellIcon
    },
    {
        icon: CircleCheckIcon,
        animation: "draw"
    },
    {
        animation: "signature",
        icon: Trash2Icon
    },
    {
        icon: RefreshCwIcon,
        animation: "spin"
    },
    {
        animation: "pulse",
        icon: ZapIcon
    }
];

const DELAY_ICONS = [
    BellIcon,
    HeartIcon,
    StarIcon,
    SparklesIcon
];

const LAST_DELAY_INDEX = DELAY_ICONS.length - 1;

const CELL_STYLE = {
    flexDirection: "column",
    alignItems: "center",
    display: "flex",
    gap: 8
} as const;

const PAIR_STYLE = {
    alignItems: "center",
    display: "flex",
    gap: 16
} as const;

const SLOT_STYLE = {
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    height: 32
} as const;

const SampleCell = ({
    children,
    label
}: {
    children: ReactNode;
    label: string;
}) => <div
    style={CELL_STYLE}
>
    <div
        style={SLOT_STYLE}
    >
        {children}
    </div>
    <Text
        variant="bodySmallSize"
        color="low"
    >
        {label}
    </Text>
</div>;

const createHostIcon = ({
    animationTrigger,
    animation,
    icon
}: {
    animationTrigger: AnimatedIconTrigger;
    animation: AnimatedIconAnimation;
    icon: LucideIcon;
}): NCoreUIKitIcon => {
    const HostIcon: NCoreUIKitIcon = ({
        customColor,
        color,
        size
    }) => <AnimatedIcon
        animationTrigger={animationTrigger}
        customColor={customColor}
        animation={animation}
        color={color}
        icon={icon}
        size={size}
    />;

    return HostIcon;
};

const ANIMATION_BUTTONS = ANIMATIONS.map(({
    animation,
    icon
}) => ({
    host: createHostIcon({
        animationTrigger: "press",
        animation,
        icon
    }),
    animation
}));

const HoverIcon = createHostIcon({
    animationTrigger: "hover",
    animation: "signature",
    icon: Trash2Icon
});

const PressIcon = createHostIcon({
    animationTrigger: "press",
    animation: "pop",
    icon: HeartIcon
});

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("animatedIconPage.animationsTitle"),
        id: "animations"
    },
    {
        title: t("animatedIconPage.triggersTitle"),
        id: "triggers"
    },
    {
        title: t("animatedIconPage.transitionsTitle"),
        id: "transitions"
    },
    {
        title: t("animatedIconPage.delayTitle"),
        id: "delay"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const AnimatedIconPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        durations
    } = NCoreUIKitTheme.useContext();

    const [
        playCount,
        setPlayCount
    ] = useState(0);

    const [
        isLooping,
        setIsLooping
    ] = useState(false);

    const [
        isToggled,
        setIsToggled
    ] = useState(false);

    const [
        transitionCount,
        setTransitionCount
    ] = useState(0);

    const [
        delayPlayCount,
        setDelayPlayCount
    ] = useState(0);

    const [
        endCount,
        setEndCount
    ] = useState(0);

    const handleTransitionEnd = () => {
        setTransitionCount((current) => current + 1);
    };

    const handleAnimationEnd = () => {
        setEndCount((current) => current + 1);
    };

    return <Article>
        <ComponentHeader
            lead={t("animatedIconPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("animatedIconPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("animatedIconPage.overviewCaption")}
            >
                <AnimatedIcon
                    animation="wiggle"
                    key={playCount}
                    icon={BellIcon}
                    size="large"
                />
                <Button
                    onPress={() => setPlayCount(playCount + 1)}
                    title={t("animatedIconPage.replay")}
                    spreadBehaviour="free"
                    variant="outline"
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("animatedIconPage.animationsTitle")}
            id="animations"
        >
            <Paragraph>
                {t("animatedIconPage.animationsText")}
            </Paragraph>
            <Preview>
                {
                    ANIMATION_BUTTONS.map(({
                        animation,
                        host
                    }) => <Button
                        onPress={() => undefined}
                        title={animation}
                        variant="outline"
                        key={animation}
                        icon={host}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("animatedIconPage.triggersTitle")}
            id="triggers"
        >
            <Paragraph>
                {t("animatedIconPage.triggersText")}
            </Paragraph>
            <Preview
                caption={t("animatedIconPage.triggersCaption")}
            >
                <Button
                    onPress={() => undefined}
                    spreadBehaviour="free"
                    variant="outline"
                    icon={HoverIcon}
                    title="hover"
                />
                <Button
                    onPress={() => undefined}
                    spreadBehaviour="free"
                    variant="outline"
                    icon={PressIcon}
                    title="press"
                />
                <div
                    style={PAIR_STYLE}
                >
                    <Switch
                        title={t("animatedIconPage.loopSwitch")}
                        onCheckedChange={setIsLooping}
                        spreadBehaviour="free"
                        isChecked={isLooping}
                    />
                    <AnimatedIcon
                        animation={isLooping ? "trace" : "none"}
                        animationTrigger="loop"
                        icon={SearchIcon}
                        size="large"
                    />
                </div>
            </Preview>
            <CodeBlock
                code={TRIGGER_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("animatedIconPage.transitionsTitle")}
            id="transitions"
        >
            <Paragraph>
                {t("animatedIconPage.transitionsText")}
            </Paragraph>
            <Preview
                caption={t("animatedIconPage.transitionsCaption", [
                    transitionCount
                ])}
            >
                <SampleCell
                    label="menu ↔ x"
                >
                    <AnimatedIcon
                        onTransitionEnd={handleTransitionEnd}
                        isTransitioned={isToggled}
                        iconTransition="morph"
                        fromIcon={MenuIcon}
                        toIcon={XIcon}
                        size="large"
                    />
                </SampleCell>
                <SampleCell
                    label="eye ↔ eye-closed"
                >
                    <AnimatedIcon
                        isTransitioned={isToggled}
                        toIcon={EyeClosedIcon}
                        iconTransition="morph"
                        fromIcon={EyeIcon}
                        size="large"
                    />
                </SampleCell>
                <SampleCell
                    label="swap"
                >
                    <AnimatedIcon
                        icon={isToggled ? HeartIcon : ThumbsUpIcon}
                        iconTransition="swap"
                        size="large"
                    />
                </SampleCell>
                <Button
                    onPress={() => setIsToggled(!isToggled)}
                    title={t("animatedIconPage.toggle")}
                    spreadBehaviour="free"
                    variant="outline"
                />
            </Preview>
        </Section>
        <Section
            title={t("animatedIconPage.delayTitle")}
            id="delay"
        >
            <Paragraph>
                {t("animatedIconPage.delayText")}
            </Paragraph>
            <Preview
                caption={t("animatedIconPage.delayCaption", [
                    endCount
                ])}
            >
                {
                    DELAY_ICONS.map((icon, index) => <AnimatedIcon
                        onAnimationEnd={index === LAST_DELAY_INDEX ? handleAnimationEnd : undefined}
                        animationDelay={index * durations.short / 2}
                        key={`${delayPlayCount}-${index}`}
                        animation="appear"
                        size="large"
                        icon={icon}
                    />)
                }
                <Button
                    onPress={() => setDelayPlayCount(delayPlayCount + 1)}
                    title={t("animatedIconPage.replay")}
                    spreadBehaviour="free"
                    variant="outline"
                />
            </Preview>
            <CodeBlock
                code={DELAY_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("animatedIconPage.a11yDecorative"),
                    t("animatedIconPage.a11yMotion"),
                    t("animatedIconPage.a11yTrigger"),
                    t("animatedIconPage.a11yName"),
                    t("animatedIconPage.a11yWarnings")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("animatedIconPage.propIcon"),
                        type: "LucideIcon",
                        name: "icon"
                    },
                    {
                        description: t("animatedIconPage.propFromIcon"),
                        type: "LucideIcon",
                        name: "fromIcon"
                    },
                    {
                        description: t("animatedIconPage.propToIcon"),
                        type: "LucideIcon",
                        name: "toIcon"
                    },
                    {
                        description: t("animatedIconPage.propIsTransitioned"),
                        name: "isTransitioned",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("animatedIconPage.propIconTransition"),
                        type: "\"none\" | \"swap\" | \"morph\"",
                        name: "iconTransition"
                    },
                    {
                        type: "\"none\" | \"draw\" | \"appear\" | \"pop\" | \"wiggle\" | \"spin\" | \"pulse\" | \"trace\" | \"signature\"",
                        description: t("animatedIconPage.propAnimation"),
                        defaultValue: "\"none\"",
                        name: "animation"
                    },
                    {
                        description: t("animatedIconPage.propAnimationTrigger"),
                        type: "\"mount\" | \"hover\" | \"press\" | \"loop\"",
                        name: "animationTrigger"
                    },
                    {
                        description: t("animatedIconPage.propAnimationDelay"),
                        name: "animationDelay",
                        defaultValue: "0",
                        type: "number"
                    },
                    {
                        description: t("animatedIconPage.propOnAnimationEnd"),
                        name: "onAnimationEnd",
                        type: "() => void"
                    },
                    {
                        description: t("animatedIconPage.propOnTransitionEnd"),
                        name: "onTransitionEnd",
                        type: "() => void"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\" | number",
                        description: t("animatedIconPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("animatedIconPage.propColor"),
                        type: "keyof IconContentColors",
                        defaultValue: "\"mid\"",
                        name: "color"
                    },
                    {
                        description: t("animatedIconPage.propCustomColor"),
                        type: "keyof ProjectColorPalette | string",
                        name: "customColor"
                    },
                    {
                        description: t("animatedIconPage.propIsMirrored"),
                        name: "isMirrored",
                        type: "boolean"
                    },
                    {
                        description: t("animatedIconPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("animatedIconPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("animatedIconPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default AnimatedIconPage;
